"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.askUserAnswersSchema = exports.askUserAnswerSchema = exports.askUserInputSchema = exports.askUserQuestionSchema = exports.NOT_ANSWERED_REASON = exports.ASK_USER_TOOL_NAME = void 0;
exports.isAnswerFor = isAnswerFor;
const zod_1 = require("zod");
/** The name the model calls the question tool by. */
exports.ASK_USER_TOOL_NAME = 'ask_user';
/**
 * What the model is told when the person sent a new message instead of answering: the result of
 * the call that asked, so it knows it asked and got nothing back.
 */
exports.NOT_ANSWERED_REASON = 'The person did not answer and sent a new message instead. Go on without these answers, or ask again if you still need them.';
/**
 * What the input offers beside the options, in each language it is shown in, so an option of that
 * name would show twice.
 */
const OTHER = new Set(['other', 'otra', 'outra']);
const distinct = (values) => new Set(values.map((value) => value.trim().toLowerCase())).size === values.length;
const optionSchema = zod_1.z.object({
    label: zod_1.z
        .string()
        .trim()
        .min(1)
        .max(80)
        .refine((label) => !OTHER.has(label.toLowerCase()), {
        message: 'Leave "Other" out: the person can always type their own answer',
    }),
    description: zod_1.z.string().trim().max(200).optional(),
});
exports.askUserQuestionSchema = zod_1.z.object({
    /** A chip naming the question, short enough to sit beside it. */
    header: zod_1.z.string().trim().min(1).max(12),
    question: zod_1.z.string().trim().min(1).max(500),
    multiSelect: zod_1.z.boolean(),
    // Told apart by label in the answers and on screen.
    options: zod_1.z
        .array(optionSchema)
        .min(2)
        .max(4)
        .refine((options) => distinct(options.map((option) => option.label)), {
        message: 'Give each option a different label',
    }),
});
exports.askUserInputSchema = zod_1.z.object({
    questions: zod_1.z
        .array(exports.askUserQuestionSchema)
        .min(1)
        .max(4)
        .refine((questions) => distinct(questions.map((question) => question.question)), {
        message: 'Ask each question once',
    }),
});
exports.askUserAnswerSchema = zod_1.z.object({
    question: zod_1.z.string(),
    selected: zod_1.z.array(zod_1.z.string()),
    /** What the person typed instead of, or beside, a choice. */
    other: zod_1.z.string().trim().min(1).max(1000).optional(),
});
exports.askUserAnswersSchema = zod_1.z.object({
    answers: zod_1.z.array(exports.askUserAnswerSchema).min(1).max(4),
});
/**
 * Whether `answers` answer exactly these questions: each one once, each choice among its options,
 * a single-select with at most one choice, and every question given a choice or typed text.
 */
function isAnswerFor(questions, answers) {
    if (answers.answers.length !== questions.length)
        return false;
    return questions.every((question) => {
        const answer = answers.answers.find((a) => a.question === question.question);
        if (!answer)
            return false;
        const labels = new Set(question.options.map((option) => option.label));
        if (!answer.selected.every((label) => labels.has(label)))
            return false;
        if (new Set(answer.selected).size !== answer.selected.length)
            return false;
        if (!question.multiSelect && answer.selected.length > 1)
            return false;
        return answer.selected.length > 0 || answer.other !== undefined;
    });
}
